import type { AttachmentPlanningDigestSnapshot } from '../../services/ai/attachment/attachmentPlanningIntelligence'
import { ProBadge } from '../Common'

interface AttachmentIntelligencePanelProps {
  digest: AttachmentPlanningDigestSnapshot | null
  status: 'idle' | 'analyzing' | 'ready' | 'error'
  error?: string | null
  sourceCount?: number
  compact?: boolean
}

export const AttachmentIntelligencePanel = ({
  digest,
  status,
  error,
  sourceCount = 0,
  compact = false,
}: AttachmentIntelligencePanelProps) => {
  if (!digest && status === 'idle' && !error && sourceCount === 0) return null

  const primaryQuestion = digest?.followUpQuestions[0]?.question ?? null
  const sourcePreview = digest?.sources.slice(0, compact ? 2 : 3).map((source) => source.label).join(', ') ?? ''
  const visibleResources = digest?.inferred.resources.slice(0, compact ? 2 : 3) ?? []
  const visibleConstraints = digest?.inferred.constraints.slice(0, compact ? 1 : 2) ?? []
  const visibleTaskLanes = digest?.planOutline.taskLanes.slice(0, compact ? 2 : 3) ?? []

  return (
    <section
      className={`aiMagicAttachmentIntelCard${status === 'analyzing' ? ' isAnalyzing' : ''}${status === 'error' ? ' isFallback' : ''}${compact ? ' isCompact' : ''}`}
      aria-live="polite"
    >
      <div className="aiMagicAttachmentIntelHead">
        <div>
          <ProBadge label="Premium" size="sm" />
          <strong>Attachment intelligence</strong>
        </div>
        <span className="aiMagicAttachmentIntelStatus">
          {status === 'analyzing'
            ? 'Reading sources'
            : digest
              ? 'Ready'
              : status === 'error'
                ? 'Fallback'
                : 'Standby'}
        </span>
      </div>
      <p className="aiMagicAttachmentIntelSummary">
        {status === 'analyzing'
          ? 'Pulling dates, resources, recurring hints, and checklist patterns out of the attached files before Line drafts anything.'
          : digest?.summary
            ?? 'Line will read the attachments first, extract planning signals your app can use, then only ask follow-ups when something still changes the draft materially.'}
      </p>
      {digest && (
        <>
          <div className="aiMagicAttachmentIntelSignals">
            {sourceCount > 0 && <span>Sources: {sourceCount}</span>}
            {digest.inferred.deadlineText && <span>Deadline: {digest.inferred.deadlineText}</span>}
            {digest.inferred.timeframeText && <span>Window: {digest.inferred.timeframeText}</span>}
            {visibleResources.length > 0 && <span>Resources: {visibleResources.join(', ')}</span>}
            {visibleConstraints.length > 0 && <span>Constraints: {visibleConstraints.join('; ')}</span>}
          </div>
          {(visibleTaskLanes.length > 0 || primaryQuestion || digest.sources[0]) && (
            compact ? (
              <div className="aiMagicAttachmentIntelCompact">
                {visibleTaskLanes.length > 0 && (
                  <p>
                    <strong>Likely lanes:</strong> {visibleTaskLanes.join(', ')}
                  </p>
                )}
                {(primaryQuestion || sourcePreview) && (
                  <p>
                    <strong>{primaryQuestion ? 'Only if still needed:' : 'Sources in context:'}</strong> {primaryQuestion ?? sourcePreview}
                  </p>
                )}
              </div>
            ) : (
              <div className="aiMagicAttachmentIntelBody">
                {visibleTaskLanes.length > 0 && (
                <div className="aiMagicAttachmentIntelColumn">
                  <span>Likely lanes</span>
                  <ul>
                    {visibleTaskLanes.map((lane) => (
                      <li key={lane}>{lane}</li>
                    ))}
                  </ul>
                </div>
              )}
                {(primaryQuestion || digest.sources[0]) && (
                  <div className="aiMagicAttachmentIntelColumn">
                    <span>{primaryQuestion ? 'Only if still needed' : 'Sources in context'}</span>
                    {primaryQuestion
                      ? <p>{primaryQuestion}</p>
                      : <p>{sourcePreview}</p>}
                  </div>
                )}
              </div>
            )
          )}
        </>
      )}
      {error && (
        <p className="aiMagicAttachmentIntelFallback">
          Falling back to the raw attachment context for this turn: {error}
        </p>
      )}
    </section>
  )
}
